import Seo from "../components/Seo";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import NotionsBackground from "../components/NotionsBackground";
import LevelIndicator from "../components/LevelIndicator";
import { RetroCard, RowArrows, ScrollRow, SignupHint, ThemeCard } from "../components/CourseBits";
import { FilterButton, FilterGroup, FilterSheet } from "../components/FilterSheet";
import { useCourseProgress, useCoursesData } from "../lib/courseData";
import { COURSE_LEVELS, LETTERS, firstLetter, normalize, sortRetros } from "../lib/courseMeta";
const LEVEL_FILTERS = [{ id: "all", label: "Tous niveaux" }, ...COURSE_LEVELS];
const KIND_STYLE = {
  Glossaire: "text-sky-300 bg-sky-500/10 border-sky-500/30",
  Thématique: "text-orange-300 bg-orange-500/10 border-orange-500/30",
  Concept: "text-orange-300 bg-orange-500/10 border-orange-500/30",
  Rétrospective: "text-violet-300 bg-violet-500/10 border-violet-500/30",
};
/** Recherche avec suggestions : cours, rétrospectives, thématiques et définitions du glossaire. */
function CourseSearch({ courses, themes }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const index = useMemo(() => {
    const items = themes.map((t) => ({ kind: "Thématique", title: t.title, sub: `${t.courses.length} cours`, to: `/cours/thematique/${t.slug}` }));
    courses.forEach((c) => {
      if (c.category === "vocabulary") items.push({ kind: "Glossaire", title: c.title, sub: c.description, to: `/cours/${c.slug}` });
      else if (c.category === "concept") items.push({ kind: "Concept", title: c.title, sub: themes.find((t) => t.courses.includes(c))?.title || "Concept", level: c.level, to: `/cours/${c.slug}` });
      else items.push({ kind: "Rétrospective", title: c.title, sub: c.period || "Rétrospective", level: c.level, to: `/cours/${c.slug}` });
    });
    return items;
  }, [courses, themes]);
  const results = useMemo(() => {
    const n = normalize(q.trim());
    if (!n) return [];
    const scored = [];
    index.forEach((it) => {
      const t = normalize(it.title);
      let s = -1;
      if (t.startsWith(n)) s = 0;
      else if (t.split(/[^a-z0-9]+/).some((w) => w.startsWith(n))) s = 1;
      else if (t.includes(n)) s = 2;
      else if (n.length >= 3 && normalize(it.sub).includes(n)) s = 3;
      if (s >= 0) scored.push([s, it]);
    });
    scored.sort((a, b) => a[0] - b[0] || a[1].title.length - b[1].title.length);
    return scored.slice(0, 7).map((x) => x[1]);
  }, [q, index]);
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const go = (it) => { setOpen(false); navigate(it.to); };
  const highlight = (title) => {
    const n = normalize(q.trim()), i = normalize(title).indexOf(n);
    if (!n || i < 0) return title;
    return (<>{title.slice(0, i)}<span className="text-orange-300 font-semibold">{title.slice(i, i + n.length)}</span>{title.slice(i + n.length)}</>);
  };
  return (
    <div className="relative flex-1 min-w-0 w-full md:flex-none md:max-w-md" ref={wrapRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => q && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && results.length) { e.preventDefault(); setActive((a) => (a + 1) % results.length); }
          else if (e.key === "ArrowUp" && results.length) { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); }
          else if (e.key === "Enter" && results[active]) { e.preventDefault(); go(results[active]); }
          else if (e.key === "Escape") setOpen(false);
        }}
        placeholder="Rechercher un cours ou un mot…"
        className="w-full h-11 rounded-md pl-9 pr-9 bg-white/[0.03] border border-white/10 text-sm placeholder:text-zinc-500 focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/15"
        role="combobox"
        aria-expanded={open && !!q.trim()}
        aria-controls="course-suggest"
        autoComplete="off"
        data-testid="course-search-input"
      />
      {q && (
        <button type="button" onClick={() => { setQ(""); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded text-zinc-500 hover:text-white" aria-label="Effacer">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      {open && q.trim() && (
        <div id="course-suggest" role="listbox" className="absolute z-40 top-full mt-2 left-0 right-0 rounded-xl border border-white/10 bg-[#121214] shadow-2xl overflow-hidden" data-testid="course-suggestions">
          {results.length ? (
            <>
              <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-zinc-500">Suggestions</div>
              {results.map((r, i) => (
                <button
                  key={`${r.kind}-${r.to}`}
                  type="button"
                  role="option"
                  aria-selected={i === active}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(r)}
                  className={`w-full text-left px-4 py-2.5 flex items-center gap-3 ${i === active ? "bg-white/5" : ""}`}
                >
                  <span className={`shrink-0 w-[96px] text-center px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-[0.1em] ${KIND_STYLE[r.kind]}`}>{r.kind}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-zinc-100 truncate">{highlight(r.title)}</span>
                    <span className="block text-xs text-zinc-500 truncate">{r.sub}</span>
                  </span>
                  {r.level && <LevelIndicator level={r.level} className="hidden sm:inline-flex shrink-0" />}
                </button>
              ))}
              <div className="px-4 py-2 border-t border-white/5 text-[11px] text-zinc-500">↑ ↓ pour naviguer · Entrée pour ouvrir</div>
            </>
          ) : (
            <div className="px-4 py-5 text-sm text-zinc-500">Aucun résultat pour « {q} ».</div>
          )}
        </div>
      )}
    </div>
  );
}
function RowHeader({ eyebrow, title, count, sub, arrows }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-5">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-orange-400 mb-1">{eyebrow}</div>
        <h2 className="font-display font-black text-2xl">
          {title} <span className="ml-1 align-middle text-sm font-normal text-zinc-500">{count}</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-1">{sub}</p>
      </div>
      {arrows}
    </div>
  );
}
const Empty = ({ children }) => (
  <div className="rounded-xl border border-dashed border-white/10 py-10 text-center text-sm text-zinc-500 w-full shrink-0">{children}</div>
);
export default function CoursesPage() {
  const [level, setLevel] = useState("all");
  const [sheet, setSheet] = useState(false); // téléphone : panneau des niveaux
  const { courses, themes, isLoading } = useCoursesData();
  const { loggedIn, readIds } = useCourseProgress();
  const okLevel = (c) => level === "all" || c.level === level;
  const shownThemes = themes.filter((t) => t.courses.some(okLevel));
  const retros = sortRetros(courses.filter((c) => c.category === "article" && okLevel(c)));
  const terms = courses.filter((c) => c.category === "vocabulary");
  const letters = new Set(terms.map((t) => firstLetter(t.title)));
  const lvlTxt = level === "all" ? "" : ` de niveau ${COURSE_LEVELS.find((l) => l.id === level).label.toLowerCase()}`;
  return (
    <div className="min-h-screen">
      <Seo title="Cours" description="Apprenez à investir avec les cours Side B : concepts, rétrospectives et glossaire, du débutant au confirmé." path="/cours" />
      <section className="relative border-b border-white/5 overflow-hidden">
        <NotionsBackground />
        <div className="relative mx-auto max-w-7xl px-6 py-8 sm:py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-2 sm:mb-3">Espace apprentissage</div>
          <h1 className="font-display font-black text-[2rem] leading-tight sm:text-5xl">Cours</h1>
          <p className="mt-2 sm:mt-4 text-[15px] sm:text-base text-zinc-400 max-w-2xl">
            Les notions clés, les grands épisodes de l'histoire des marchés et un glossaire. Du niveau débutant à avancé,
            à votre rythme.
          </p>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 pt-6 sm:pt-10 pb-10 sm:pb-12">
        {/* Téléphone : recherche + bouton « Filtres » (niveaux) sur une ligne ; ordinateur : niveaux à droite */}
        <div className="flex items-center gap-2 md:gap-3">
          <CourseSearch courses={courses} themes={themes} />
          <div className="md:hidden">
            <FilterButton accent="orange" count={level !== "all" ? 1 : 0} onClick={() => setSheet(true)} testid="course-filter-button" />
          </div>
          <div className="hidden md:flex items-center gap-1 border border-white/10 rounded-lg p-1 bg-white/[0.02] overflow-x-auto md:ml-auto md:shrink-0" data-testid="course-levels">
            {LEVEL_FILTERS.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => setLevel(l.id)}
                data-testid={`course-level-${l.id}`}
                className={`shrink-0 whitespace-nowrap rounded-md px-3 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors ${
                  level === l.id ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <FilterSheet open={sheet} onClose={() => setSheet(false)} onReset={() => setLevel("all")} accent="orange" resultLabel="Voir les cours">
          <FilterGroup label="Niveau" options={LEVEL_FILTERS} value={level} onChange={setLevel} accent="orange" />
        </FilterSheet>
        {!loggedIn && <div className="mt-4"><SignupHint /></div>}
        {isLoading ? (
          <div className="mt-12 flex gap-5 overflow-hidden">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-64 w-[300px] shrink-0 rounded-xl bg-white/[0.02] border border-white/5 animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <div className="mt-12" data-testid="course-concepts">
              <div className="-mx-6">
                <ScrollRow
                  id="row-concepts"
                  controls={(scroll, edges) => (
                    <div className="px-6">
                      <RowHeader
                        eyebrow="Comprendre"
                        title="Concepts"
                        count={`${shownThemes.length} thématique${shownThemes.length > 1 ? "s" : ""}`}
                        sub="Des parcours par thématique, dans l'ordre de lecture conseillé."
                        arrows={<RowArrows onScroll={scroll} edges={edges} />}
                      />
                    </div>
                  )}
                >
                  {shownThemes.length ? (
                    shownThemes.map((t) => <ThemeCard key={t.id} theme={t} readIds={readIds} loggedIn={loggedIn} level={level} />)
                  ) : (
                    <Empty>Aucun concept{lvlTxt} pour l'instant.</Empty>
                  )}
                </ScrollRow>
              </div>
            </div>
            <div className="mt-14" data-testid="course-retros">
              <div className="-mx-6 relative">
                <ScrollRow
                  id="row-retros"
                  className="relative"
                  controls={(scroll, edges) => (
                    <div className="px-6">
                      <RowHeader
                        eyebrow="Retenir"
                        title="Rétrospectives"
                        count={`${retros.length} rétrospective${retros.length > 1 ? "s" : ""}`}
                        sub="Les grands épisodes des marchés, dans l'ordre chronologique."
                        arrows={<RowArrows onScroll={scroll} edges={edges} />}
                      />
                    </div>
                  )}
                >
                  {retros.length ? (
                    <>
                      <div className="absolute left-6 right-6 top-[5.5px] h-px bg-gradient-to-r from-orange-500/40 via-white/10 to-white/5 pointer-events-none" aria-hidden="true" />
                      {retros.map((c) => <RetroCard key={c.id} course={c} isRead={loggedIn && readIds.has(c.id)} />)}
                    </>
                  ) : (
                    <Empty>Aucune rétrospective{lvlTxt} pour l'instant.</Empty>
                  )}
                </ScrollRow>
              </div>
            </div>
            <div className="mt-14" data-testid="course-glossary">
              <div className="mb-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-orange-400 mb-1">Vocabulaire</div>
                <h2 className="font-display font-black text-2xl">
                  Glossaire <span className="ml-1 align-middle text-sm font-normal text-zinc-500">{terms.length} définition{terms.length > 1 ? "s" : ""}</span>
                </h2>
                <p className="text-sm text-zinc-400 mt-1">Les mots de la finance, expliqués simplement. Choisissez une lettre.</p>
              </div>
              <div className="grid grid-cols-7 sm:grid-cols-9 lg:flex gap-1.5">
                {LETTERS.map((L) =>
                  letters.has(L) ? (
                    <Link
                      key={L}
                      to={`/cours/glossaire/${L.toLowerCase()}`}
                      className="h-10 min-w-[2.25rem] flex-1 grid place-items-center rounded-md text-sm font-semibold text-zinc-200 border border-white/5 bg-white/[0.02] hover:bg-orange-500/15 hover:border-orange-500/30 hover:text-orange-300 transition-colors"
                      data-testid={`glossary-letter-${L}`}
                    >
                      {L}
                    </Link>
                  ) : (
                    <span key={L} className="h-10 min-w-[2.25rem] flex-1 grid place-items-center rounded-md text-sm text-zinc-700" title="Aucune définition">{L}</span>
                  )
                )}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
